// src/app/about/about.component.ts
import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SideNavComponent } from '../side-nav/side-nav.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { SettingsComponent } from '../settings/settings.component';
import { FooterNavComponent } from '../footer-nav/footer-nav.component';

import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-about',
  standalone: true,
  imports: [CommonModule, SideNavComponent, TranslateModule, RouterModule, SettingsComponent, FooterNavComponent],
  templateUrl: './about.component.html',
})
export class AboutComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mobileHeader') mobileHeader!: ElementRef<HTMLDivElement>;

  showFooterMenu = false;
  showSettings = false;

  /** Hauteur réelle du header mobile (mesurée) + petite marge.
   *  Fallback à 96px pour éviter que la première card “colle” si la mesure tarde. */
  headerHpx = 80;

  private _subs: Subscription[] = [];

  constructor(private translate: TranslateService, private ngZone: NgZone) { }

  // == THEME / MODE ==
  isDarkMode(): boolean {
    return document.body.classList.contains('dark-mode');
  }
  get invBridgeGradient(): string {
    return 'bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700';
  }
  get invBridgeLogo(): string {
    return 'text-purple-500';
  }
  toggleTheme() {
    const body = document.body;
    if (body.classList.contains('light-mode')) {
      body.classList.remove('light-mode');
      body.classList.add('dark-mode');
      localStorage.setItem('theme', 'dark');
    } else {
      body.classList.remove('dark-mode');
      body.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
    }
    // Le layout change (polices/rendus), on met à jour la hauteur
    setTimeout(() => this.updateHeaderHeight(), 0);
  }

  // == SETTINGS / FOOTER MENU ==
  toggleSettings() { this.showSettings = !this.showSettings; }
  toggleFooterMenu() { this.showFooterMenu = !this.showFooterMenu; }
  closeFooterMenu() { this.showFooterMenu = false; }

  // == LANG ==
  get selectedLanguage(): string {
    const l = this.translate.currentLang || localStorage.getItem('lang') || 'en';
    return l.toLowerCase();
  }

  // == CURRENCY CHANGE (placeholder conservé) ==
  onCurrencyChanged(code: string) {
    // Pas d’appel à fetchPrices / CoinGecko ici.
  }

  // == HEADER HEIGHT MEASURE ==
  ngAfterViewInit(): void {
    // Mesure initiale (une fois le header rendu)
    setTimeout(() => this.updateHeaderHeight(), 0);

    // Listeners resize & orientation (hors zone Angular + debounce)
    this.ngZone.runOutsideAngular(() => {
      const subResize = fromEvent(window, 'resize')
        .pipe(debounceTime(120))
        .subscribe(() => this.updateHeaderHeight());

      const subOrient = fromEvent(window as any, 'orientationchange')
        .pipe(debounceTime(120))
        .subscribe(() => this.updateHeaderHeight());

      this._subs.push(subResize, subOrient);
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  private updateHeaderHeight(): void {
    const el = this.mobileHeader?.nativeElement;
    if (!el) return;

    // offsetHeight inclut le padding (safe-area iOS compris)
    const measured = el.offsetHeight;

    // Micro-marge pour éviter que la première card ne “colle” visuellement
    const EXTRA = 2;  

    this.ngZone.run(() => (this.headerHpx = Math.max(el.offsetHeight + EXTRA, 80)));
  }
}
